/**
 * Payment Method Helper Utilities
 * Shared helper functions for payment method operations
 */

import type { PaymentMethod } from '@/types/residents/payment';

/**
 * Check if a payment method is a bank account
 */
export const isPaymentMethodBank = (method: any): boolean => {
  return (
    method?.type === 'bankAccount' ||
    method?.type === 'ach' ||
    method?.type === 'bank' ||
    method?.accttype === 'ECHK' ||
    method?.accttype === 'ESAV' ||
    method?.accttype === 'PBEBIT'
  );
};

/**
 * Sort payment methods with default first
 */
export const sortPaymentMethodsByDefault = <T extends { isDefault?: boolean }>(methods: T[]): T[] => {
  return [...methods].sort(
    ({ isDefault: x = false }, { isDefault: y = false }) =>
      Number(y) - Number(x)
  );
};

/**
 * Get the source ID from a payment method
 * Handles both sourceId and id properties
 */
export const getPaymentMethodSourceId = (method: any): number => {
  return Number(method?.sourceId) || Number(method?.id) || 0;
};

/**
 * Unified function to check if a payment method card is expired
 * Handles multiple expiry formats: MMYY, MM/YY, MM/YYYY
 * 
 * @param expiryOrMethod - Either an expiry string or a payment method object
 * @returns boolean indicating if the card is expired
 */
export const isPaymentMethodExpired = (expiryOrMethod: string | any): boolean => {
  // Handle payment method object
  const expiry = typeof expiryOrMethod === 'string' 
    ? expiryOrMethod 
    : expiryOrMethod?.expiry || expiryOrMethod?.expiryMonth && expiryOrMethod?.expiryYear 
      ? `${String(expiryOrMethod.expiryMonth).padStart(2, '0')}/${expiryOrMethod.expiryYear}`
      : null;
  
  if (!expiry) return false;
  
  try {
    let mm: number, yy: number;
    
    if (expiry.includes('/')) {
      const [month, year] = expiry.split('/');
      mm = Number(month);
      yy = Number(year);
    } else if (expiry.length === 4) {
      // MMYY format
      mm = Number(expiry.substring(0, 2));
      yy = Number(expiry.substring(2));
    } else {
      return false;
    }
    
    if (!mm || !yy || mm < 1 || mm > 12) return false;
    
    // Handle 2-digit vs 4-digit year
    const expYear = yy < 100 ? 2000 + yy : yy;
    
    // End of expiry month (last day, last millisecond)
    const expiryDate = new Date(expYear, mm, 0, 23, 59, 59, 999);
    
    return Date.now() > expiryDate.getTime();
  } catch {
    return false;
  }
};

/**
 * Check if payment method will expire within N days
 */
export const isPaymentMethodExpiringSoon = (method: any, daysThreshold = 30): boolean => {
  if (!method?.expiry) return false;
  
  if (isPaymentMethodExpired(method)) return false;
  
  try {
    const expiry = method.expiry;
    let mm: number, yy: number;
    
    if (expiry.includes('/')) {
      const [month, year] = expiry.split('/');
      mm = Number(month);
      yy = Number(year);
    } else if (expiry.length === 4) {
      mm = Number(expiry.substring(0, 2));
      yy = Number(expiry.substring(2));
    } else {
      return false;
    }
    
    const expYear = yy < 100 ? 2000 + yy : yy;
    const expiryDate = new Date(expYear, mm, 0);
    
    const thresholdDate = new Date();
    thresholdDate.setDate(thresholdDate.getDate() + daysThreshold);
    
    return expiryDate < thresholdDate;
  } catch {
    return false;
  }
};

/**
 * Normalize a payment method object to the canonical PaymentMethod type
 */
export const normalizePaymentMethod = (method: any): PaymentMethod | null => {
  if (!method) return null;
  
  const isBank = isPaymentMethodBank(method);
  
  return {
    id: Number(method.id) || 0,
    sourceId: Number(method.sourceId) || Number(method.id) || 0,
    type: isBank ? 'ach' : 'card',
    last4: method.last4 || '',
    name: method.name,
    brand: method.brand || method.accttype,
    isDefault: Boolean(method.isDefault),
    isExpired: method.isExpired ?? isPaymentMethodExpired(method),
    expiryMonth: method.expiryMonth || method.expMonth,
    expiryYear: method.expiryYear || method.expYear,
    expiry: method.expiry,
    bankName: method.bankName || method.name,
    accttype: method.accttype,
    blockedAt: method.blockedAt,
    isDebitCard: method.isDebitCard,
  };
};
